import { DtConstants } from '../schema/DtConstants';
import { encodeFullId } from '../encoding/elementIdAsFullId';
import { createOrUpdateSpace, Operation } from "./CreateSpace";
import { FacetTypes } from "../facets/Facets";
import { ElementFlags, QC } from "../schema/dt-schema";
import { AGGREGATE_ISOLATION_CHANGED_EVENT, AGGREGATE_SELECTION_CHANGED_EVENT } from "../../application/EventTypes";
import { DT_SKETCH_TOOL_CHANGED_EVENT } from "../DtEventTypes";
import { EventDispatcher } from "../../application/EventDispatcher";
import * as dte from "../DtEventTypes";
import i18n from 'i18next';

const av = Autodesk.Viewing;

function fuzzyEquals(a, b, eps = 1e-1) {
  return (
    Math.abs(a.x - b.x) < eps &&
    Math.abs(a.y - b.y) < eps &&
    Math.abs(a.z - b.z) < eps);

}

export class SketchToolsControls extends EventDispatcher {

  /**
   * @param {DtFacility} facility
   */
  constructor(facility) {
    super();
    this.facility = facility;
    this.viewer = null;
    this.edit2dExt = null;

    this.toolBarButton = null;
    this.spaceIds = new Set();
    this.modes = ["draw", "edit"];
    this.activeMode = null;
    this.removedShape = null;
  }

  async #loadEdit2DExtension() {
    const edit2dExtName = 'Autodesk.Edit2D';

    const edit2dExt = this.viewer.getExtension(edit2dExtName);
    if (!edit2dExt) {
      await this.viewer.loadExtension(edit2dExtName, { is3d: true });
      this.edit2dExt = this.viewer.getExtension(edit2dExtName);
    } else {
      this.edit2dExt = edit2dExt;
    }
  }

  /**
   * @param {Autodesk.Viewing.Viewer3D} viewer
   */
  async init(viewer) {
    this.viewer = viewer;
    this.hasViewerToolbar = Autodesk.Viewing.GuiViewer3D && viewer instanceof Autodesk.Viewing.GuiViewer3D;

    if (this.hasViewerToolbar) {
      await this.viewer.waitForToolbar();
    }
  }

  async activate(mode = 'draw') {
    let defaultModel = this.facility.getDefaultModel();
    if (!defaultModel) {
      defaultModel = await this.facility.createDefaultModel();
    }

    if (!defaultModel.isWorldBBoxSet()) {
      await defaultModel.resetWorldBBoxForDefaultModel();
      this.facility.unloadModel(defaultModel);
      this.facility.loadModel(defaultModel);
      await defaultModel.waitForLoad(false, true);
    }
    await this.#loadEdit2DExtension();

    const xform = defaultModel.getData().placementWithOffset;
    this.#setupEditingTools(xform);

    const _activate = () => {
      const urn = this.facility.getDefaultModel().urn();
      this.facility.facetsManager.setModelVisibility(urn, true);
      this.setMode(mode);

      const aggregateSelection = this.viewer.getAggregateSelection();
      const selection = aggregateSelection.find((v) => v.model.isDefault());
      if (selection) {
        const model = selection.model;
        if (selection.selection.length === 1) {
          const dbId = selection.selection[0];
          this.#selectSpaceGeometry(model, dbId);
        }
      }
    };

    if (this.#isTopDownView()) {
      _activate();
    } else {
      let xform;

      let bbox = new THREE.Box3();

      // combine bboxes
      const models = this.facility.getModels();
      for (let model of models) {
        if (model.isPrimaryModel()) {
          xform = model.myData.placementWithOffset;
        }

        if (!this.facility.isModelVisible(model)) {
          continue;
        }

        model.invalidateBBoxes();
        bbox.union(model.getVisibleBounds());
      }

      // clone final camera view
      const dstView = this.viewer.impl.camera.clone();

      // set target
      const center = bbox.getCenter(dstView.target);

      // set position
      const size = bbox.size();
      const dist = Math.max(size.x, size.y, size.z);
      const viewDir = { x: 0, y: 0, z: 1 };
      dstView.position.set(center.x + viewDir.x * dist, center.y + viewDir.y * dist, center.z + viewDir.z * dist);

      // set and transform up
      let up = new THREE.Vector3(0, 1, 0);
      if (xform) {
        up.transformDirection(xform);
      }
      dstView.up.copy(up);

      this.viewAnimControl?.stop();

      const onFinished = () => _activate();
      this.viewAnimControl = Autodesk.Viewing.Private.flyToView(this.viewer, dstView, 2, onFinished, false);
    }

    return true;
  }

  deactivate() {
    if (!this.activeMode) {
      return;
    }

    let ctx = this.edit2dExt.defaultContext;
    ctx.undoStack.removeEventListener(Autodesk.Edit2D.UndoStack.BEFORE_ACTION, this.boundOnBeforeAction);
    ctx.undoStack.removeEventListener(Autodesk.Edit2D.UndoStack.AFTER_ACTION, this.boundOnAfterAction);
    ctx.selection.removeEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_CHANGED, this.boundOnToolSelectionChanged);
    ctx.selection.removeEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_HOVER_CHANGED, this.boundOnToolHoverSelectionChanged);
    this.viewer.removeEventListener(AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChange);
    this.viewer.removeEventListener(AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChange);

    this.edit2dExt.undoStack.clear();
    this.edit2dExt.undoStack.clearListeners();
    this.edit2dExt.unregisterDefaultTools();
    this.activeMode = null;
    this.removedShape = null;
    this.viewer.dispatchEvent({ type: DT_SKETCH_TOOL_CHANGED_EVENT, active: false, mode: null });
  }

  #isTopDownView() {
    const camera = this.viewer.impl.camera;
    const target = camera.target.clone();

    const dir = target.sub(camera.position).normalize();
    return fuzzyEquals(dir, { x: 0.0, y: 0.0, z: -1.0 });
  }

  #setupEditingTools(placementXform) {
    this.boundOnBeforeAction = this.#onBeforeAction.bind(this);
    this.boundOnAfterAction = this.#onAfterAction.bind(this);
    this.boundOnToolSelectionChanged = this.#onToolSelectionChanged.bind(this);
    this.boundOnToolHoverSelectionChanged = this.#onToolHoverSelectionChanged.bind(this);
    this.boundOnSelectionChange = this.#onSelectionChanged.bind(this);
    this.boundOnIsolationChange = this.#onIsolationChanged.bind(this);

    this.edit2dExt.registerDefaultTools();

    let ctx = this.edit2dExt.defaultContext;
    ctx.setMatrix(placementXform);

    if (!ctx.undoStack.hasEventListener(Autodesk.Edit2D.UndoStack.BEFORE_ACTION, this.boundOnBeforeAction)) {
      ctx.undoStack.addEventListener(Autodesk.Edit2D.UndoStack.BEFORE_ACTION, this.boundOnBeforeAction);
    }
    if (!ctx.undoStack.hasEventListener(Autodesk.Edit2D.UndoStack.AFTER_ACTION, this.boundOnAfterAction)) {
      ctx.undoStack.addEventListener(Autodesk.Edit2D.UndoStack.AFTER_ACTION, this.boundOnAfterAction);
    }

    // Reacts to selection changes for sketched polygons
    if (!ctx.selection.hasEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_CHANGED, this.boundOnToolSelectionChanged)) {
      ctx.selection.addEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_CHANGED, this.boundOnToolSelectionChanged);
    }
    if (!ctx.selection.hasEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_HOVER_CHANGED, this.boundOnToolHoverSelectionChanged)) {
      ctx.selection.addEventListener(Autodesk.Edit2D.Selection.Events.SELECTION_HOVER_CHANGED, this.boundOnToolHoverSelectionChanged);
    }

    // Reacts to general selection changes
    if (!this.viewer.hasEventListener(AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChange)) {
      this.viewer.addEventListener(AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChange);
    }

    if (!this.viewer.hasEventListener(AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChange)) {
      this.viewer.addEventListener(AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChange);
    }
  }

  #onBeforeAction(...params) {

    //console.log(params);
  }
  #onAfterAction({ action }) {
    const actions = Autodesk.Edit2D.Actions;

    if (action instanceof actions.AddVertex || action instanceof actions.MoveVertex || action instanceof actions.RemoveVertex ||
    action instanceof actions.MoveEdge || action instanceof actions.MoveShapes) {
      return true;
    }

    if (action instanceof actions.RemoveShape) {
      this.#removeShape(action.shape);
      return;
    }

    const cp = this.edit2dExt.defaultTools.polygonTool.canvasPos;
    const vp = this.viewer.impl.clientToViewport(cp.x, cp.y);

    let idHit = [];
    let dbId = this.viewer.impl.renderer().idAtPixel(vp.x, vp.y, idHit);
    const modelId = idHit[1];

    let model;
    if (modelId > 0) {
      model = this.viewer.impl.modelQueue().findModel(modelId);
    } else {
      let intersections = [];
      this.viewer.impl.hitTestViewport(vp.clone(), false, null, null, intersections);
      intersections = intersections.filter((a) => a.model);

      if (intersections.length > 0) {
        dbId = intersections[0].dbId;
        model = intersections[0].model;
      }
    }

    if (!model) {
      this.#onError(new Error(i18n.t("Cannot find a model to get level information")));
      return;
    }

    const level = this.#getLevel(model, dbId);
    console.log("Level: ", level);

    // create action has a property "shape" assigned to it, editing actions have "poly"
    let shape = action.shape || action.poly;
    if (!shape) {
      this.#onError(new Error(i18n.t("Missing polygon after editing action")));
      return;
    }

    shape.customData = {
      dbId: dbId,
      model: model,
      level: level,
      op: Operation.CREATING
    };

    this.#addShape(shape);
  }

  #onError(error) {
    console.error("Error in SketchToolsControls:", error);
    this.dispatchEvent({ type: dte.DT_SKETCH_TOOL_ERROR_EVENT, error });
  }

  #onToolSelectionChanged({ target }) {
    this.#selectShape(target);
  }

  #onToolHoverSelectionChanged(...params) {

    //console.log(params);
  }
  #onSelectionChanged({ selections }) {
    let ctx = this.edit2dExt.defaultContext;

    // check for the default model being selected
    const selection = selections[0];
    if (selections.length === 1 && selection.model.isDefault()) {
      let toBeSelected = [];

      // check for selected space (if any)
      const shape = ctx.layer.shapes[0];
      const oldSelectedDbId = shape?.customData?.dbId;
      if (oldSelectedDbId > 0 && selection.dbIdArray.length > 1) {
        toBeSelected = selection.dbIdArray.filter((dbId) => dbId !== oldSelectedDbId);
      }

      ctx.layer.clear();
      ctx.gizmoLayer.clear();

      const dbId = toBeSelected[0] || selection.dbIdArray[0];
      this.#selectSpaceGeometry(selection.model, dbId);

      if (oldSelectedDbId > 0) {
        selection.model.selector.deselectNodes([oldSelectedDbId]);
      }
    } else if (selections.length === 0) {
      // nothing selected, just clear edit2d layers
      ctx.layer.clear();
      ctx.gizmoLayer.clear();
    }
  }

  #onIsolationChanged() {
    let ctx = this.edit2dExt.defaultContext;
    ctx.layer.clear();
    ctx.gizmoLayer.clear();
  }

  #getLevel(model, dbIdAtPixel) {
    // Get level from enabled filter if possible
    const facetDefs = this.facility.facetsManager.getFacetDefs();
    const levelFacetIdx = this.facility.facetsManager.getFacetDefIndex(FacetTypes.levels);
    const filter = facetDefs[levelFacetIdx].filter;
    if (filter.size === 1) {
      const levelName = filter.values().toArray()[0];
      const level = Object.values(model.getLevels()).find((l) => l.name === levelName);
      if (level) {
        return level;
      }
    }

    // Get level from a surface we were sketching on
    const levelId = model.myData.dbId2levelId[dbIdAtPixel];
    return model.myData.levelMap[levelId];
  }

  #removeShape(shape) {
    const { cdata: { op } } = shape;
    if (op === Operation.EDITING) this.removedShape = shape;
    this.setMode('draw');
  }

  #selectShape(selection) {
    if (selection.hoveredId !== 0) {
      this.selectedShape = Object.values(selection.isSelected)[0];
    } else {
      this.selectedShape = null;
    }
  }

  #selectSpaceGeometry(model, dbId) {
    const dbId2flags = model.getData().dbId2flags;
    if (dbId2flags[dbId] === ElementFlags.Room) {
      model.getSketchedGeometries(dbId).then((res) => {
        let centerline, elevation, height;

        for (const prop of res.element.properties) {
          if (prop.internalName === QC.Centerline) {
            centerline = prop.displayValue;
          } else
          if (prop.internalName === QC.Elevation) {
            elevation = prop.displayValue;
          } else
          if (prop.internalName === QC.Height) {
            height = prop.displayValue;
          }
        }

        const elevationExists = elevation != null;

        if (centerline && elevationExists) {
          let shape = Autodesk.Edit2D.Shape.fromSVG(centerline);

          // "Layer.addShape()" method expects shape elevation in layer coordinates
          // and has to be aligned with current plane constant value
          let gizmoLayer = this.edit2dExt.defaultTools.polygonEditTool.gizmoLayer;

          const xform = gizmoLayer.origWorldToLayer;
          const c = gizmoLayer.plane.constant;
          shape.z += elevation - xform.elements[14] + c;
          gizmoLayer.addShape(shape);

          this.edit2dExt.defaultTools.polygonEditTool.layer.addShape(shape);

          // all level infos are already copied into the default model, safe to usem them here
          const levelId = model.myData.dbId2levelId[dbId];

          shape.customData = {
            dbId: dbId,
            model: model,
            level: model.myData.levelMap[levelId],
            op: Operation.EDITING
          };

          if (height) {
            shape.customData['existingHeight'] = height;
          }

          this.#addShape(shape);
        } else {
          if (!centerline && !elevationExists) {
            this.#onError(new Error(i18n.t("Space boundary and elevation are missing")));
          } else if (!centerline) {
            this.#onError(new Error(i18n.t("Space boundary is missing")));
          } else {
            this.#onError(new Error(i18n.t("Space elevation is missing")));
          }
        }
      });
    }
  }

  #addShape(shape) {
    let ctx = this.edit2dExt.defaultContext;
    ctx.selection.setSelection([shape]);

    this.selectedShape = shape;
    this.setMode('edit');
  }

  setMode(mode) {
    if (!this.modes.includes(mode)) {
      console.log(`Mode ${mode} is not supported.`);
      return;
    }

    if (this.activeMode === mode) {
      return;
    }

    this.activeMode = mode;
    switch (mode) {
      case "draw":
        this.#activateDrawingTool();
        break;
      case "edit":
        this.#activateEditingTool();
        break;
      default:
        break;
    }

    this.viewer.dispatchEvent({ type: DT_SKETCH_TOOL_CHANGED_EVENT, active: !!this.activeMode, mode });
  }

  #activateDrawingTool() {
    this.selectedShape = null;
    let ctx = this.edit2dExt.defaultContext;
    ctx.layer.clear();
    ctx.gizmoLayer.clear();

    const defaultModel = this.facility.getDefaultModel();
    defaultModel.selector?.clearSelection();

    let controller = this.viewer.toolController;
    controller.setIsLocked(false);
    if (controller.isToolActivated(this.edit2dExt.defaultTools.polygonEditTool.getName())) {
      controller.deactivateTool(this.edit2dExt.defaultTools.polygonEditTool.getName());
    }

    controller.activateTool(this.edit2dExt.defaultTools.polygonTool.getName());
  }

  #activateEditingTool() {
    let controller = this.viewer.toolController;
    controller.setIsLocked(false);
    if (controller.isToolActivated(this.edit2dExt.defaultTools.polygonTool.getName())) {
      controller.deactivateTool(this.edit2dExt.defaultTools.polygonTool.getName());
    }
    controller.activateTool(this.edit2dExt.defaultTools.polygonEditTool.getName());
    this.edit2dExt.defaultTools.polygonEditTool.onSelectionChanged();
  }

  async saveShape() {
    // if user has removed a shape during editing, delete the element
    if (this.removedShape) {
      const { cdata: { dbId, model } } = this.removedShape;

      await this.facility.delete({ [model.urn()]: [dbId] });

      this.viewer.setAggregateSelection([]);
      this.removedShape = null;
      return;
    }

    if (!this.selectedShape) {
      return;
    }

    let layer = this.edit2dExt.defaultContext.layer;

    try {
      const res = await createOrUpdateSpace(this.facility, this.viewer, layer, this.selectedShape);
      res.forEach((id) => this.spaceIds.add(id));
      this.startRoomAssignmentTask();
    } catch (err) {
      this.#onError(err);
      return;
    }

    layer.removeShape(this.selectedShape);

    this.setMode('draw');
  }

  startRoomAssignmentTask() {
    const externalSpaceIds = Array.from(this.spaceIds);
    if (externalSpaceIds?.length > 0) {
      const defaultModel = this.facility.getDefaultModel();
      const modelGuid = defaultModel.urn().slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);

      // encode full ids
      const ids = externalSpaceIds.map((id) => encodeFullId(modelGuid, id));
      this.facility.assignRooms(
        this.facility.settings.boundByUpperFloor,
        this.facility.settings.skipRoomBounders,
        ids
      );
    }
  }
}